import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import API_URLS from "../../../core/common/apiUrls";
import AirplaneLoader from "../flight-loader/flightLoader";
import OfferPopup, { AppliedCoupon } from "../../../core/common/OfferPopup";

interface RazorpayResponse {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

interface BaggageDetails {
  airline_code: string;
  baggage_id: number;
  code: string;
  created_at: string;
  currency: string;
  description: number;
  destination: string;
  flight_number: string;
  origin: string;
  price: number;
  review_passenger_id: number;
  way_type: number;
  weight: number;
}

interface MealDetails {
  airline_code: string;
  airline_description: string;
  code: string;
  created_at?: string;
  currency: string;
  description: number;
  destination: string;
  flight_number: string;
  meal_id?: number;
  origin: string;
  price: number;
  quantity: number;
  review_passenger_id?: number;
  way_type: number;
}

interface SeatDetails {
  airline_code: string;
  availability_type: number;
  code: string;
  compartment: number;
  craft_type: string;
  created_at: string;
  currency: string;
  deck: number;
  description: number;
  destination: string;
  flight_number: string;
  origin: string;
  price: number;
  review_passenger_id: number;
  row_no: string;
  seat_id: number;
  seat_no: string | null;
  seat_type: number;
  seat_way_type: number;
}

interface FareDetails {
  BaseFare: number;
  Currency: string;
  SSRAmount: number;
  Tax: number;
  TotalAmount: number;
  YQTax: number;
}

interface Passenger {
  BaggageDetails: BaggageDetails[];
  City: string;
  ContactNo: string;
  DateOfBirth: string;
  Email: string;
  FareDetails: FareDetails;
  FirstName: string;
  Gender: number;
  IsLeadPax: boolean;
  LastName: string;
  MealDetails: MealDetails[];
  Nationality: string;
  PassportExpiry: string | null;
  PassportNo: string;
  PaxId: number;
  PaxType: number;
  SeatDetails: SeatDetails[];
  Title: string;
}

interface Segment {
  AirlineCode: string;
  ArrivalDateTime: string;
  DepartureDateTime: string;
  Destination: string;
  FlightNumber: string;
  Origin: string;
  SegmentIndicator: number;
  TripIndicator: number;
}

interface Leg {
  Currency: string;
  FlightOfferId: number;
  LegIndex: number;
  Passengers: Passenger[];
  ReviewId: number;
  Segments: Segment[];
  TotalAmount: number;
}

interface FareSummary {
  BaseFareTotal: number;
  ConvenienceFee: number;
  Currency: string;
  GrandTotalAmount: number;
  InsuranceAmount: number;
}

interface ReviewPassengersResponse {
  FareSummary: FareSummary;
  Legs: Leg[];
  TripType: string;
  code: number;
  message: string;
}

const CONVENIENCE_FEE_PER_TICKET = 50;

const FlightBooking = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const location = useLocation();

  const [passengerData, setPassengerData] = useState<ReviewPassengersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flightDetails, setFlightDetails] = useState<any>(null);
  const [offersVisible, setOffersVisible] = useState<boolean>(false);
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null);
  const [showLockModal, setShowLockModal] = useState(false);

  const isHoldMode: boolean = location.state?.isHoldMode ?? false;
  const isDomestic: boolean = location.state?.isDomestic ?? true;
  const [depositPerPax, setDepositPerPax] = useState<number>(isDomestic ? 250 : 500);

  useEffect(() => {
    fetch(API_URLS.FLIGHTS.LOCK_PRICE_CONFIG)
      .then(r => r.json())
      .then(d => {
        const rate = isDomestic ? d?.domestic_deposit_per_pax : d?.international_deposit_per_pax;
        if (rate) setDepositPerPax(Number(rate));
      })
      .catch(() => {});
  }, [isDomestic]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchPassengerDetails = async () => {
      try {
        const { savedPassengersDetails, flightDetails: flightDetailsFromState, isHoldMode: holdModeFromState } = location.state || {};

        if (!savedPassengersDetails || !flightDetailsFromState) {
          throw new Error("Missing passenger or flight details");
        }

        setFlightDetails(flightDetailsFromState);

        let reviewIds: number[] = [];
        if (Array.isArray(savedPassengersDetails.ReviewIds)) {
          reviewIds = savedPassengersDetails.ReviewIds.map((item: any) => item.review_id);
        } else if (typeof savedPassengersDetails.ReviewId === 'number') {
          reviewIds = [savedPassengersDetails.ReviewId];
        } else if (savedPassengersDetails.ReviewId && typeof savedPassengersDetails.ReviewId === 'object') {
          reviewIds = [savedPassengersDetails.ReviewId.review_id];
        } else {
          throw new Error("Invalid ReviewId format in saved passenger details");
        }

        const response = await fetch(API_URLS.FLIGHTS.REVIEW_PASSENGERS, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ EndUserIp: "106.219.87.88", ReviewId: reviewIds }),
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const data: ReviewPassengersResponse = await response.json();
        if (data.code !== 200) throw new Error(data.message || "Failed to fetch passenger details");

        setPassengerData(data);
        setLoading(false);
      } catch (err: any) {
        console.error("Error fetching passenger details:", err);
        setError(err.message || "Failed to load passenger details");
        setLoading(false);
      }
    };

    fetchPassengerDetails();
  }, [location.state]);

  const handlePayment = async () => {
    try {
      if (!passengerData) {
        alert("Please wait while passenger details are being loaded");
        return;
      }

      const payableAmount = appliedCoupon
        ? appliedCoupon.final_amount
        : passengerData.FareSummary.GrandTotalAmount;

      const response = await fetch(API_URLS.PAYMENTS.CREATE_ORDER, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: payableAmount,
          currency: "INR",
          coupon_code: appliedCoupon?.code || null,
          discount_amount: appliedCoupon?.discount_amount || 0,
        }),
      });

      const data = await response.json();
      const { order_id, razorpay_key_id, amount, currency } = data;

      if (!window.Razorpay) {
        alert("Razorpay SDK not loaded");
        return;
      }

      let leadPassenger: Passenger | undefined;
      passengerData.Legs.forEach(leg => {
        const lead = leg.Passengers.find(p => p.IsLeadPax);
        if (lead) leadPassenger = lead;
      });

      const options = {
        key: razorpay_key_id,
        amount: String(amount),
        currency: currency,
        name: "Flight Booking",
        description: "Flight Booking Payment",
        order_id,
        handler: async function (razorResponse: RazorpayResponse) {
          const verifyRes = await fetch(API_URLS.PAYMENTS.VERIFY_PAYMENT, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(razorResponse),
          });
          const verifyData = await verifyRes.json();

          if (verifyData.status === "success") {
            if (appliedCoupon?.code) {
              fetch(API_URLS.COUPONS.USE, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ code: appliedCoupon.code }),
              }).catch(() => {});
            }
            navigate(routes.flightBookingConfirmation, {
              state: {
                temp_id: location.state?.savedPassengersDetails?.TempId,
                payment_id: verifyData.payment_id,
              },
            });
          } else {
            alert("Payment verification failed. Please try again.");
          }
        },
        theme: { color: "#3399cc" },
        prefill: {
          name: `${leadPassenger?.FirstName || ""} ${leadPassenger?.LastName || ""}`,
          email: leadPassenger?.Email || "",
          contact: leadPassenger?.ContactNo || "",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (err: any) {
        console.error("Payment failed:", err.error);
        alert("Payment failed. Please try again.");
      });
      rzp.open();
    } catch (err) {
      console.error("Payment error:", err);
      alert("Something went wrong. Please try again.");
    }
  };

  const handleHoldPayment = async () => {
    try {
      if (!passengerData) {
        alert("Please wait while passenger details are being loaded");
        return;
      }

      const numPassengers = passengerData.Legs[0]?.Passengers.length || 1;
      const totalConvenienceFee = CONVENIENCE_FEE_PER_TICKET * numPassengers;
      const totalChargeNow = depositPerPax * numPassengers + totalConvenienceFee;

      const response = await fetch(API_URLS.PAYMENTS.CREATE_ORDER, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: totalChargeNow,
          currency: "INR",
          coupon_code: null,
          discount_amount: 0,
        }),
      });

      const data = await response.json();
      const { order_id, razorpay_key_id, amount, currency } = data;

      if (!window.Razorpay) {
        alert("Razorpay SDK not loaded");
        return;
      }

      let leadPassenger: Passenger | undefined;
      passengerData.Legs.forEach(leg => {
        const lead = leg.Passengers.find(p => p.IsLeadPax);
        if (lead) leadPassenger = lead;
      });

      const options = {
        key: razorpay_key_id,
        amount: String(amount),
        currency: currency,
        name: "Lock Flight Price",
        description: `Lock deposit ₹${depositPerPax * numPassengers} + convenience ₹${totalConvenienceFee} — rest paid later`,
        order_id,
        handler: async function (razorResponse: RazorpayResponse) {
          const verifyRes = await fetch(API_URLS.PAYMENTS.VERIFY_PAYMENT, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(razorResponse),
          });
          const verifyData = await verifyRes.json();

          if (verifyData.status === "success") {
            navigate(routes.flightBookingConfirmation, {
              state: {
                temp_id: location.state?.savedPassengersDetails?.TempId,
                payment_id: verifyData.payment_id,
                isHoldMode: true,
                holdDepositAmount: totalChargeNow,
                totalFare: passengerData?.FareSummary?.GrandTotalAmount,
              },
            });
          } else {
            alert("Payment verification failed. Please try again.");
          }
        },
        theme: { color: "#f59e0b" },
        prefill: {
          name: `${leadPassenger?.FirstName || ""} ${leadPassenger?.LastName || ""}`,
          email: leadPassenger?.Email || "",
          contact: leadPassenger?.ContactNo || "",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (err: any) {
        console.error("Hold payment failed:", err.error);
        alert("Payment failed. Please try again.");
      });
      rzp.open();
    } catch (err) {
      console.error("Hold payment error:", err);
      alert("Something went wrong. Please try again.");
    }
  };

  const getPassengerType = (paxType: number) => {
    switch (paxType) {
      case 1: return "Adult";
      case 2: return "Child";
      case 3: return "Infant";
      default: return "Passenger";
    }
  };

  const getGenderText = (gender: number) => gender === 1 ? "Male" : "Female";

  const getLegTitle = (legIndex: number, tripType: string) => {
    if (tripType === "oneway") return "Flight";
    if (tripType === "roundtrip") return legIndex === 0 ? "Outbound Flight" : "Return Flight";
    return `Flight ${legIndex + 1}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-IN', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatTime = (dateString: string) =>
    new Date(dateString).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });

  const formatDateShort = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

  const flightDuration = (dep: string, arr: string) =>
    Math.floor((new Date(arr).getTime() - new Date(dep).getTime()) / 60000);

  if (loading) {
    return (
      <div>
        <div className="breadcrumb-bar flightList-banner-bg text-center">
          <div className="container">
            <div className="row">
              <div className="col-md-12 col-12">
                <h2 className="breadcrumb-title mb-2">Review Details</h2>
              </div>
            </div>
          </div>
        </div>
        <div className="flight-loading">
          <AirplaneLoader text="Loading passenger details..." size="large" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="breadcrumb-bar flightList-banner-bg text-center">
          <div className="container">
            <div className="row">
              <div className="col-md-12 col-12">
                <h2 className="breadcrumb-title mb-2">Review Details</h2>
              </div>
            </div>
          </div>
        </div>
        <div className="flight-error">
          <div className="error-content">
            <i className="isax isax-close-circle"></i>
            <h4>Something went wrong</h4>
            <p>{error}</p>
            <button className="btn btn-primary mt-3" onClick={() => navigate(-1)}>
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const lastTicketDate = flightDetails?.Fare?.Results?.[0]?.LastTicketDate;
  const holdDeadline = lastTicketDate
    ? new Date(lastTicketDate).toLocaleString('en-IN', {
        weekday: 'short', day: 'numeric', month: 'short',
        year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    : null;

  return (
    <div>
      <div className="breadcrumb-bar flightList-banner-bg text-center">
        <div className="container">
          <div className="row">
            <div className="col-md-12 col-12">
              <h2 className="breadcrumb-title mb-2">Review Details</h2>
            </div>
          </div>
        </div>
      </div>

      <div className="content content-two">
        <div className="container">
          <div className="row">
            {/* Left — Passenger & Flight Details */}
            <div className="col-lg-8">
              {/* Passenger Details Card */}
              <div className="card shadow-sm mb-4">
                <div className="card-header bg-light">
                  <h5 className="mb-0">
                    <i className="isax isax-user me-2"></i> Passenger Details
                  </h5>
                </div>
                <div className="card-body">
                  {passengerData?.Legs.map((leg, legIndex) => (
                    <div key={legIndex} className={legIndex > 0 ? "mt-4 pt-4 border-top" : ""}>
                      {passengerData.Legs.length > 1 && (
                        <p className="text-muted small mb-2 fw-medium">{getLegTitle(legIndex, passengerData.TripType)}</p>
                      )}
                      {leg.Passengers.map((passenger, passengerIndex) => (
                        <div key={passengerIndex} className={passengerIndex < leg.Passengers.length - 1 ? "mb-3 pb-3 border-bottom" : ""}>

                          {/* Tags row */}
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <span className="badge rounded-pill px-3 py-1" style={{ background: '#e8f0fe', color: '#1a56db', fontSize: 12 }}>
                              {passenger.IsLeadPax ? "Lead Passenger" : `Passenger ${passengerIndex + 1}`}
                            </span>
                            <span className="badge rounded-pill px-3 py-1" style={{ background: '#f3f4f6', color: '#6b7280', fontSize: 12 }}>
                              {getPassengerType(passenger.PaxType)}
                            </span>
                          </div>

                          {/* Name & Gender inline */}
                          <div className="d-flex flex-wrap gap-4 mb-1">
                            <span className="small">
                              <span className="text-muted">Name: </span>
                              <span className="fw-medium text-dark">{passenger.Title} {passenger.FirstName} {passenger.LastName}</span>
                            </span>
                            <span className="small">
                              <span className="text-muted">Gender: </span>
                              <span className="fw-medium text-dark">{getGenderText(passenger.Gender)}</span>
                            </span>
                          </div>

                          {/* Email & Contact inline */}
                          <div className="d-flex flex-wrap gap-4 mb-2">
                            <span className="small">
                              <span className="text-muted">Email: </span>
                              <span className="fw-medium text-dark">{passenger.Email}</span>
                            </span>
                            <span className="small">
                              <span className="text-muted">Contact: </span>
                              <span className="fw-medium text-dark">{passenger.ContactNo}</span>
                            </span>
                          </div>

                          {/* Fare row */}
                          <div className="d-flex flex-wrap gap-3 align-items-center pt-2 mt-1 border-top">
                            <span className="small text-muted">Base Fare: <strong className="text-dark">₹{passenger.FareDetails.BaseFare.toLocaleString()}</strong></span>
                            <span className="text-muted" style={{ fontSize: 10 }}>+</span>
                            <span className="small text-muted">Tax: <strong className="text-dark">₹{passenger.FareDetails.Tax.toLocaleString()}</strong></span>
                            {passenger.FareDetails.SSRAmount > 0 && (
                              <>
                                <span className="text-muted" style={{ fontSize: 10 }}>+</span>
                                <span className="small text-muted">SSR: <strong className="text-dark">₹{passenger.FareDetails.SSRAmount.toLocaleString()}</strong></span>
                              </>
                            )}
                            <span className="ms-auto small fw-semibold text-primary">Total: ₹{passenger.FareDetails.TotalAmount.toLocaleString()}</span>
                          </div>

                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {/* Flight Details Card */}
              {passengerData?.Legs && (
                <>
                  {passengerData.Legs.map((leg, legIndex) => (
                    <div className="card shadow-sm mb-3" key={legIndex}>
                      <div className="card-header bg-light">
                        <h6 className="mb-0">
                          <i className="isax isax-airplane me-2"></i>
                          {getLegTitle(legIndex, passengerData.TripType)}
                          {passengerData.Legs.length > 1 && ` (Review ID: #${leg.ReviewId})`}
                        </h6>
                      </div>
                      <div className="card-body px-4 py-3">
                        {leg.Segments.map((segment, segmentIndex) => {
                          const dur = flightDuration(segment.DepartureDateTime, segment.ArrivalDateTime);
                          const hrs = Math.floor(dur / 60);
                          const mins = dur % 60;
                          return (
                          <div key={segmentIndex} className={segmentIndex > 0 ? "mt-4 pt-4 border-top" : ""}>
                            {/* Airline row */}
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <div className="d-flex align-items-center gap-2">
                                <span className="d-flex align-items-center justify-content-center rounded bg-primary text-white"
                                  style={{ width: 32, height: 32, flexShrink: 0 }}>
                                  <i className="isax isax-airplane" style={{ fontSize: 15 }} />
                                </span>
                                <div>
                                  <div className="fw-bold text-dark">{segment.AirlineCode} {segment.FlightNumber}</div>
                                  <div className="text-muted" style={{ fontSize: 11 }}>Economy</div>
                                </div>
                              </div>
                              <span className="badge rounded-pill text-dark fw-medium px-3 py-2"
                                style={{ background: '#f0f0f0', fontSize: 12 }}>
                                <i className="isax isax-clock me-1" style={{ fontSize: 13 }} />
                                {hrs > 0 ? `${hrs}h ` : ''}{mins}m
                              </span>
                            </div>

                            {/* Route display */}
                            <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                              {/* Departure */}
                              <div style={{ minWidth: 0, flex: '0 0 auto' }}>
                                <div className="fw-bold" style={{ fontSize: 26, lineHeight: 1 }}>{formatTime(segment.DepartureDateTime)}</div>
                                <div className="fw-semibold text-primary mt-1">{segment.Origin}</div>
                                <div className="text-muted mt-1" style={{ fontSize: 12 }}>{formatDateShort(segment.DepartureDateTime)}</div>
                              </div>

                              {/* Flight path line */}
                              <div className="flex-fill d-flex flex-column align-items-center px-2" style={{ minWidth: 0 }}>
                                <div className="text-muted mb-1" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                                  {hrs > 0 ? `${hrs}h ` : ''}{mins}m non-stop
                                </div>
                                <div className="d-flex align-items-center w-100">
                                  <div className="rounded-circle bg-primary" style={{ width: 7, height: 7, flexShrink: 0 }} />
                                  <div className="flex-fill" style={{ height: 2, background: 'linear-gradient(90deg, #0d6efd 0%, #0d6efd 100%)', opacity: 0.3 }} />
                                  <i className="isax isax-airplane text-primary" style={{ fontSize: 18, flexShrink: 0 }} />
                                  <div className="flex-fill" style={{ height: 2, background: 'linear-gradient(90deg, #0d6efd 0%, #0d6efd 100%)', opacity: 0.3 }} />
                                  <div className="rounded-circle bg-primary" style={{ width: 7, height: 7, flexShrink: 0 }} />
                                </div>
                              </div>

                              {/* Arrival */}
                              <div className="text-end" style={{ minWidth: 0, flex: '0 0 auto' }}>
                                <div className="fw-bold" style={{ fontSize: 26, lineHeight: 1 }}>{formatTime(segment.ArrivalDateTime)}</div>
                                <div className="fw-semibold text-primary mt-1">{segment.Destination}</div>
                                <div className="text-muted mt-1" style={{ fontSize: 12 }}>{formatDateShort(segment.ArrivalDateTime)}</div>
                              </div>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Right — Payment Summary */}
            <div className="col-lg-4">
              <div className="card shadow-sm p-3 sticky-top" style={{ top: "20px" }}>
                <h5 className="mb-3">
                  {isHoldMode ? <><i className="isax isax-lock me-2 text-warning"></i>Lock Price Summary</> : "Payment Summary"}
                </h5>

                {!isHoldMode && (
                  <div className="mb-3">
                    <button className="btn btn-outline-secondary w-100 mb-2" onClick={() => setOffersVisible(true)}>
                      View Offers &amp; Coupons
                    </button>
                  </div>
                )}

                {passengerData && (
                  <>
                    <ul className="list-unstyled mb-3">
                      <li className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Trip Type</span>
                        <span className="text-capitalize">{passengerData.TripType}</span>
                      </li>
                      <li className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Number of Flights</span>
                        <span>{passengerData.Legs.length}</span>
                      </li>
                      {passengerData.Legs.map((leg, index) => (
                        <li key={index} className="d-flex justify-content-between mb-1 small">
                          <span className="text-muted">{getLegTitle(index, passengerData.TripType)}:</span>
                          <span>₹{leg.TotalAmount.toLocaleString()}</span>
                        </li>
                      ))}

                      <li className="d-flex justify-content-between mb-2 border-top pt-2">
                        <span className="text-muted">Base Fare Total</span>
                        <span>₹{passengerData.FareSummary.BaseFareTotal.toLocaleString()}</span>
                      </li>

                      {passengerData.FareSummary.ConvenienceFee !== 0 && (
                        <li className="d-flex justify-content-between mb-2">
                          <span className="text-muted">
                            {passengerData.FareSummary.ConvenienceFee > 0 ? 'Convenience Fee' : 'Discount'}
                          </span>
                          <span>
                            {passengerData.FareSummary.ConvenienceFee > 0
                              ? "₹" + passengerData.FareSummary.ConvenienceFee.toLocaleString()
                              : "− ₹" + Math.abs(passengerData.FareSummary.ConvenienceFee).toLocaleString()}
                          </span>
                        </li>
                      )}

                      {passengerData.FareSummary.InsuranceAmount > 0 && (
                        <li className="d-flex justify-content-between mb-2 small">
                          <span className="text-muted">Insurance</span>
                          <span>₹{passengerData.FareSummary.InsuranceAmount.toLocaleString()}</span>
                        </li>
                      )}

                      {appliedCoupon && (
                        <li className="d-flex justify-content-between mb-2 text-success">
                          <span>Coupon ({appliedCoupon.code})</span>
                          <span>−₹{appliedCoupon.discount_amount.toLocaleString()}</span>
                        </li>
                      )}

                      <li className="d-flex justify-content-between fw-bold border-top pt-2 mt-2">
                        <span>Grand Total</span>
                        <span className="text-primary">
                          ₹{(appliedCoupon
                            ? appliedCoupon.final_amount
                            : passengerData.FareSummary.GrandTotalAmount
                          ).toLocaleString()}
                        </span>
                      </li>

                      {isHoldMode && (() => {
                        const numPassengers = passengerData.Legs[0]?.Passengers.length || 1;
                        const totalConvFee = CONVENIENCE_FEE_PER_TICKET * numPassengers;
                        const totalDepositNow = depositPerPax * numPassengers;
                        const totalNow = totalDepositNow + totalConvFee;
                        const remaining = passengerData.FareSummary.GrandTotalAmount - totalDepositNow;
                        return (
                          <>
                            <li className="border-top pt-3 mt-2 mb-0">
                              <p className="fw-semibold fs-13 mb-2 text-dark">Lock Price Charges</p>
                            </li>
                            <li className="d-flex justify-content-between mb-1">
                              <span className="text-muted fs-12">Lock Deposit (₹{depositPerPax.toLocaleString()} × {numPassengers} pax)</span>
                              <span className="fw-medium fs-12">₹{totalDepositNow.toLocaleString()}</span>
                            </li>
                            <li className="d-flex justify-content-between mb-1">
                              <span className="text-muted fs-12">Convenience Fee (₹{CONVENIENCE_FEE_PER_TICKET} × {numPassengers} pax)</span>
                              <span className="fw-medium fs-12">₹{totalConvFee.toLocaleString()}</span>
                            </li>
                            <li className="d-flex justify-content-between mb-2 border-top pt-2">
                              <span className="fw-semibold text-warning">Total Pay Now</span>
                              <span className="fw-bold text-warning">₹{totalNow.toLocaleString()}</span>
                            </li>
                            {holdDeadline && (
                              <li className="d-flex justify-content-between mb-1">
                                <span className="text-muted fs-12">Hold Deadline</span>
                                <span className="fw-medium fs-12">{holdDeadline}</span>
                              </li>
                            )}
                            <li className="d-flex justify-content-between mb-1">
                              <span className="text-muted fs-12">Remaining after lock</span>
                              <span className="fw-medium fs-12">₹{remaining.toLocaleString()}</span>
                            </li>
                          </>
                        );
                      })()}
                    </ul>

                    {isHoldMode ? (
                      <div className="d-flex flex-column gap-2">
                        <button
                          type="button"
                          onClick={() => setShowLockModal(true)}
                          className="btn btn-warning w-100 fw-semibold"
                          style={{ padding: "12px 24px", fontSize: "16px" }}
                        >
                          {(() => {
                              const n = passengerData.Legs[0]?.Passengers.length || 1;
                              return `🔒 Lock Price — Pay ₹${(depositPerPax * n + CONVENIENCE_FEE_PER_TICKET * n).toLocaleString()} Now`;
                            })()}
                        </button>
                        <button
                          type="button"
                          onClick={handlePayment}
                          className="btn btn-outline-primary w-100"
                          style={{ padding: "10px 24px", fontSize: "14px" }}
                        >
                          Pay Full Amount Instead
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={handlePayment}
                        className="btn btn-primary w-100"
                        style={{ padding: "12px 24px", fontSize: "16px" }}
                      >
                        Confirm &amp; Pay
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <OfferPopup
        visible={offersVisible}
        onClose={() => setOffersVisible(false)}
        onPayNow={handlePayment}
        isHotelOffers={false}
        bookingAmount={passengerData?.FareSummary.GrandTotalAmount ?? 0}
        appliedCoupon={appliedCoupon}
        onCouponApplied={setAppliedCoupon}
      />

      {/* Lock Price Confirmation Modal */}
      {showLockModal && passengerData && (() => {
        const n = passengerData.Legs[0]?.Passengers.length || 1;
        const totalConvFee = CONVENIENCE_FEE_PER_TICKET * n;
        const totalDeposit = depositPerPax * n;
        const totalNow = totalDeposit + totalConvFee;
        const totalFare = passengerData.FareSummary.GrandTotalAmount;
        const remaining = totalFare - totalDeposit;
        return (
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
            onClick={(e) => { if (e.target === e.currentTarget) setShowLockModal(false); }}
          >
            <div style={{ background: "#fff", borderRadius: 16, maxWidth: 480, width: "100%", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.28)" }}>
              {/* Modal header */}
              <div style={{ background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)", borderRadius: "16px 16px 0 0", padding: "20px 24px" }}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-3">
                    <div style={{ width: 48, height: 48, background: "rgba(255,255,255,0.2)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>🔒</div>
                    <div>
                      <h5 className="mb-0 text-white fw-bold">Lock Flight Price</h5>
                      <p className="mb-0 text-white fs-12" style={{ opacity: 0.85 }}>Pay a small deposit, complete later</p>
                    </div>
                  </div>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowLockModal(false)} />
                </div>
              </div>

              <div style={{ padding: "20px 24px" }}>
                {/* Price breakdown */}
                <div className="rounded-3 p-3 mb-4" style={{ background: "#fffbeb", border: "1px solid #fde68a" }}>
                  <h6 className="fw-semibold mb-3" style={{ color: "#78350f" }}>Payment Breakdown</h6>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted fs-13">Total Fare</span>
                    <span className="fw-semibold">₹{totalFare.toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted fs-13">Lock Deposit (₹{depositPerPax.toLocaleString()} × {n} pax)</span>
                    <span className="fw-semibold text-success">₹{totalDeposit.toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted fs-13">Convenience Fee (₹{CONVENIENCE_FEE_PER_TICKET} × {n} pax)</span>
                    <span className="fw-semibold">₹{totalConvFee.toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between border-top pt-2 mt-1">
                    <span className="fw-bold fs-15">Pay Now</span>
                    <span className="fw-bold fs-16" style={{ color: "#d97706" }}>₹{totalNow.toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between mt-1">
                    <span className="fs-12 text-muted">Pay later (remaining balance)</span>
                    <span className="fs-12 text-muted">₹{remaining.toLocaleString()}</span>
                  </div>
                </div>

                {/* Important to know */}
                <h6 className="fw-bold mb-3 text-dark">Important to Know</h6>
                <div className="d-flex flex-column gap-3 mb-4">
                  <div className="d-flex gap-3 rounded-3 p-3" style={{ background: "#fef2f2", border: "1px solid #fecaca" }}>
                    <div style={{ width: 36, height: 36, background: "#fee2e2", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>🚫</div>
                    <div>
                      <p className="fw-semibold mb-1 fs-13" style={{ color: "#991b1b" }}>Non-Refundable Deposit</p>
                      <p className="mb-0 fs-12 text-muted">Cost of Price Lock is <strong>non-refundable</strong> &amp; non-adjustable against the flight booking.</p>
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="d-flex gap-3">
                  <button type="button" className="btn btn-outline-secondary flex-fill" onClick={() => setShowLockModal(false)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-warning flex-fill fw-semibold"
                    style={{ fontSize: 15 }}
                    onClick={() => { setShowLockModal(false); handleHoldPayment(); }}
                  >
                    🔒 Lock — Pay ₹{totalNow.toLocaleString()}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default FlightBooking;
